<script>
    import LazyLoad from "vanilla-lazyload";
    import { onMount } from "svelte";
    import { generateRandomId } from "../utils/IdentifierGenerator.js";
    import SimpleRecommendation from "./SimpleRecommendation.svelte";
    import viewTracker from "../tracking/viewTracker.js";
    import scrollTracker from "../tracking/scrollTracker.js";

    /**
 *
 *
 *
 *
 */

    /*                */
    let {
        headline,
        tracking,
        featureIndex,
        cinemaIndex,
        trackingEventMergeId = null,
        additionalTrackingLabels = null,
        loadedTrackingActionName = null,
        recommendations = [],
        cinemaContainerMounted = Promise.resolve(),
    } = $props();

    const initialVisibleTiles = 12;
    let renderNodeTiles = $state(initialVisibleTiles);
    let alreadyTrackedIndexTiles = $state([]);
    let sourceVariationId = undefined;

    const lazyLoadConfig = {
        unobserve_entered: true,
        threshold: 0,
        callback_loaded: (el) => {
            const spinner = el.closest(".reco_simple_cinema__image")?.querySelector(".reco_simple_cinema__image__spinner_box");
            spinner?.classList.remove("p_loader250");
        },
        callback_error: (el) => {
            const spinner = el.closest(".reco_simple_cinema__image")?.querySelector(".reco_simple_cinema__image__spinner_box");
            spinner?.classList.remove("p_loader250");
        },
    };

    const trackView = (recommendations, numberOfVisibleTiles) => {
        const recommendationsTracking = recommendations.slice(0, numberOfVisibleTiles).map((recommendation) => recommendation.tracking);
        viewTracker({
            cinemaTracking: tracking,
            recommendationsTracking,
            featureIndex,
            cinemaIndex,
            eventMergeId: trackingEventMergeId,
            additionalTrackingLabels,
            actionName: loadedTrackingActionName,
        });
    };

    /*                                     */
    let simpleCinemaFeature;
    const cinemaId = `reco_cinema__${generateRandomId(16)}`;

    let visibilityPreviousActionAlreadyTracked = false;

    const trackVisibilityHorizontalScroll = (direction, tilesFeatureIds = []) => {
        const visibleRecos = recommendations.filter((reco) => tilesFeatureIds.includes(reco.tracking?.id));
        const recommendationsTracking = visibleRecos.map(({ tracking }) => tracking);
        const recommendationsMeta = visibleRecos.map(({ meta }) => meta);
        scrollTracker({
            cinemaTracking: tracking,
            recommendationsTracking,
            featureIndex,
            cinemaIndex,
            recommendationsMeta,
            scrollDirection: direction,
            sourceVariationId,
            additionalTrackingLabels,
        });
    };

    const scrollTracking = (event) => {
        if (event.target.id !== cinemaId) return;

        if (event.detail.scrollDirection !== "right") {
            if (!visibilityPreviousActionAlreadyTracked) {
                trackVisibilityHorizontalScroll("previous");
                visibilityPreviousActionAlreadyTracked = true;
            }
        } else {
            const indicesIncludingPartialTile = event.detail.visibleItems.indices.filter(
                (index) => !alreadyTrackedIndexTiles.includes(index)
            );
            const tilesFeatureIds = indicesIncludingPartialTile.map((index) => recommendations[index]?.tracking?.id);
            if (tilesFeatureIds.length > 0) {
                trackVisibilityHorizontalScroll("next", tilesFeatureIds);
                alreadyTrackedIndexTiles = [...alreadyTrackedIndexTiles, ...indicesIncludingPartialTile];
            }
        }
    };

    cinemaContainerMounted.then(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (renderNodeTiles < recommendations.length) {
                        requestAnimationFrame(() => {
                            renderNodeTiles = recommendations.length;
                        });
                    }
                    observer.unobserve(entry.target);
                }
            });
        });
        observer.observe(simpleCinemaFeature);
        window?.o_global?.events().on("squirrel.search.cinema.shown", (context) => {
            trackingEventMergeId = context.eventMergeId;
            const visibleTiles = parseInt(
                window.getComputedStyle(document.getElementById(cinemaId)).getPropertyValue("--items-per-page") || 2
            );
            alreadyTrackedIndexTiles = [...Array(visibleTiles).keys()];
            trackView(recommendations, visibleTiles);
        });

        window?.o_global?.events().on("reco-core.cinema.register", (id) => {
            window?.o_global?.events().emit("reco-core.cinema.registered", id);
        });
    });

    /*                                  */
    const lazyLoadImages = () => {
        if ("loading" in HTMLImageElement.prototype) {
            const images = document.querySelectorAll(".reco_simple_cinema__image .lazy");
            images.forEach((img) => {
                const spinner = img.closest("reco_simple_cinema__image")?.querySelector("reco_simple_cinema__image__spinner");
                spinner?.classList.remove("p_loader250");
                img.src = img.dataset.src;
            });
        } else {
            const images = document.querySelectorAll("reco_simple_cinema__image .lazy");
            const lazyLoader = new LazyLoad(lazyLoadConfig, images);
            lazyLoader.update();
        }
    };

    onMount(() => {
        lazyLoadImages();
        const cinema = document.querySelector(`#${cinemaId}`);
        cinema.addEventListener("oc-stage-scroll-completed", (event) => scrollTracking(event));
    });
</script>

<div bind:this={simpleCinemaFeature} class="reco_simple_cinema">
    <h2 class="reco_simple_cinema__headline">{headline}</h2>
    <oc-cinema-v1
        id={cinemaId}
        gap="8px"
        resizing="none"
        arrow-button-position="50px"
        class="reco_simple_cinema__carousel"
        stage-spacing="16px"
        item-visibility-threshold="0.01"
    >
        {#each recommendations as recommendation, index}
            {#if index < renderNodeTiles}
                <div class="reco_simple_cinema_tile_wrapper">
                    <SimpleRecommendation
                        {recommendation}
                        lazyLoad={index > 4}
                        cinemaTracking={tracking}
                        {featureIndex}
                        {cinemaIndex}
                        {additionalTrackingLabels}
                    />
                </div>
            {/if}
        {/each}
    </oc-cinema-v1>
</div>

<style lang="scss">
    .reco_simple_cinema {
        height: 100%;
        width: 100%;
        line-height: normal;
        padding: var(--oc-semantic-spacing-100) 0 var(--oc-semantic-spacing-100);
        container-type: inline-size;
    }

    .reco_simple_cinema__headline {
        padding-bottom: var(--oc-semantic-spacing-100);
        margin-left: var(--oc-semantic-spacing-100);
        color: var(--oc-semantic-color-text-secondary);
        font: var(--oc-semantic-font-copy-75);
    }

    .reco_simple_cinema__carousel {
        --items-per-page: 3;
    }

    /*               */
    @screen (width > 340px) {
        .reco_simple_cinema__carousel {
            --items-per-page: 4;
        }
    }

    /*               */
    @container (width > 440px) {
        .reco_simple_cinema__carousel {
            --items-per-page: 5;
        }
    }

    /*               */
    @container (width > 550px) {
        .reco_simple_cinema__carousel {
            --items-per-page: 6;
        }
    }

    /*                                           */
    @container (width > 660px) {
        .reco_simple_cinema__carousel {
            --items-per-page: 7;
        }
    }

    /*               */
    @container (width > 768px) {
        .reco_simple_cinema__carousel {
            --items-per-page: 3;
        }
    }

    /*               */
    @container (width > 777px) {
        .reco_simple_cinema__carousel {
            --items-per-page: 4;
        }
    }

    /*             */
    @container (width > 880px) {
        .reco_simple_cinema__carousel {
            --items-per-page: 5;
        }
    }
</style>
